import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import LogoutIcon from '@mui/icons-material/Logout';
import HistoryIcon from '@mui/icons-material/History';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../contexts/AuthContext';
import { DRAWER_WIDTH, ROUTES } from '../../utils/constants';
import { isVolunteerOnly } from '../../utils/helpers';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavGroup {
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

const familyNavItems: NavItem[] = [
  { label: 'Home', path: ROUTES.HOME, icon: <HomeIcon /> },
  { label: 'Events', path: ROUTES.EVENTS, icon: <EventIcon /> },
  { label: 'My Registrations', path: ROUTES.MY_REGISTRATIONS, icon: <BookmarkIcon /> },
  { label: 'Feedback', path: ROUTES.FEEDBACK, icon: <FeedbackIcon /> },
  { label: 'Profile', path: ROUTES.PROFILE, icon: <PersonIcon /> },
  { label: 'Help & Support', path: ROUTES.SUPPORT, icon: <HelpOutlineIcon /> },
];

const volunteerNavItems: NavItem[] = [
  { label: 'Volunteers', path: ROUTES.VOLUNTEERS, icon: <VolunteerActivismIcon /> },
];

const volunteerOnlyNavItems: NavItem[] = [
  { label: 'Volunteers', path: ROUTES.VOLUNTEERS, icon: <VolunteerActivismIcon /> },
  { label: 'Profile', path: ROUTES.PROFILE, icon: <PersonIcon /> },
  { label: 'Help & Support', path: ROUTES.SUPPORT, icon: <HelpOutlineIcon /> },
];

// Top-level admin item (always visible, no group)
const adminTopItem: NavItem = {
  label: 'Dashboard',
  path: ROUTES.ADMIN_DASHBOARD,
  icon: <DashboardIcon />,
};

// Grouped admin navigation
const adminNavGroups: NavGroup[] = [
  {
    label: 'Events',
    icon: <EventIcon />,
    items: [
      { label: 'Manage Events', path: ROUTES.ADMIN_EVENTS, icon: <EventIcon /> },
      { label: 'Create Event', path: ROUTES.ADMIN_CREATE_EVENT, icon: <AddCircleIcon /> },
      { label: 'Attendees', path: ROUTES.ADMIN_ATTENDEES, icon: <PeopleIcon /> },
      { label: 'Event Register', path: ROUTES.ADMIN_EVENT_REGISTER, icon: <AssignmentIcon /> },
    ],
  },
  {
    label: 'Volunteers',
    icon: <VolunteerActivismIcon />,
    items: [
      { label: 'Manage Volunteers', path: ROUTES.ADMIN_VOLUNTEERS, icon: <VolunteerActivismIcon /> },
      { label: 'Volunteer Shifts', path: ROUTES.ADMIN_VOLUNTEER_SHIFTS, icon: <AccessTimeIcon /> },
      { label: 'Volunteer Reports', path: ROUTES.ADMIN_VOLUNTEER_REPORTS, icon: <AssessmentIcon /> },
    ],
  },
  {
    label: 'Reports',
    icon: <AssessmentIcon />,
    items: [
      { label: 'Event Reports', path: ROUTES.ADMIN_REPORTS, icon: <AssessmentIcon /> },
    ],
  },
  {
    label: 'Notifications',
    icon: <NotificationsActiveIcon />,
    items: [
      { label: 'Send Notification', path: ROUTES.ADMIN_NOTIFICATIONS, icon: <NotificationsActiveIcon /> },
      { label: 'Notification Log', path: ROUTES.ADMIN_NOTIFICATION_LOG, icon: <HistoryIcon /> },
    ],
  },
  {
    label: 'Configuration',
    icon: <SettingsIcon />,
    items: [
      { label: 'Locations', path: ROUTES.ADMIN_LOCATIONS, icon: <LocationOnIcon /> },
      { label: 'Categories', path: ROUTES.ADMIN_CATEGORIES, icon: <CategoryIcon /> },
      { label: 'Settings', path: ROUTES.ADMIN_SETTINGS, icon: <SettingsIcon /> },
    ],
  },
];

const activeColor = '#00b7ed';
const activeHoverBg = 'rgba(0, 183, 237, 0.15)';
const activeBg = 'rgba(0, 183, 237, 0.1)';

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  // Track which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    // Auto-expand the group that contains the current page
    const initial: Record<string, boolean> = {};
    for (const group of adminNavGroups) {
      const hasActivePath = group.items.some(
        (item) => location.pathname === item.path || location.pathname.startsWith(item.path)
      );
      if (hasActivePath) {
        initial[group.label] = true;
      }
    }
    return initial;
  });

  const isAdmin = user?.role === 'admin';
  const navItems = isAdmin
    ? [] // admin uses grouped nav
    : isVolunteerOnly(user)
      ? volunteerOnlyNavItems
      : [...familyNavItems, ...volunteerNavItems];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
    if (isMobile) {
      onClose();
    }
  };

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const isActive = (path: string): boolean => {
    if (path === ROUTES.HOME || path === ROUTES.ADMIN_DASHBOARD) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (group: NavGroup): boolean => {
    return group.items.some((item) => isActive(item.path));
  };

  const navItemButton = (item: NavItem, nested = false) => (
    <ListItem key={item.path} disablePadding>
      <ListItemButton
        selected={isActive(item.path)}
        onClick={() => handleNavigation(item.path)}
        sx={{
          mx: 1,
          my: 0.25,
          borderRadius: 2,
          pl: nested ? 4 : 2,
          py: 0.75,
          '&.Mui-selected': {
            backgroundColor: activeBg,
            '&:hover': {
              backgroundColor: activeHoverBg,
            },
            '& .MuiListItemIcon-root': {
              color: activeColor,
            },
            '& .MuiListItemText-primary': {
              color: activeColor,
              fontWeight: 600,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: isActive(item.path) ? activeColor : 'inherit',
            '& .MuiSvgIcon-root': { fontSize: nested ? '1.2rem' : '1.4rem' },
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: nested ? '0.875rem' : '0.95rem',
            fontWeight: isActive(item.path) ? 600 : 400,
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar />
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        {isAdmin ? (
          <List disablePadding>
            {/* Dashboard - always visible at top */}
            {navItemButton(adminTopItem)}

            <Divider sx={{ mx: 2, my: 1 }} />

            {/* Grouped navigation */}
            {adminNavGroups.map((group) => (
              <React.Fragment key={group.label}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleGroup(group.label)}
                    sx={{
                      mx: 1,
                      my: 0.25,
                      borderRadius: 2,
                      py: 0.75,
                      ...(isGroupActive(group) && !expandedGroups[group.label]
                        ? {
                            '& .MuiListItemIcon-root': { color: activeColor },
                            '& .MuiListItemText-primary': { color: activeColor },
                          }
                        : {}),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isGroupActive(group) ? activeColor : 'text.secondary',
                        '& .MuiSvgIcon-root': { fontSize: '1.3rem' },
                      }}
                    >
                      {group.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={group.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: isGroupActive(group) ? activeColor : 'text.primary',
                      }}
                    />
                    {expandedGroups[group.label] ? (
                      <ExpandLess sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                    ) : (
                      <ExpandMore sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={expandedGroups[group.label]} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {group.items.map((item) => navItemButton(item, true))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <List>
            {navItems.map((item) => navItemButton(item))}
          </List>
        )}
      </Box>

      {isAuthenticated && (
        <>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 2,
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
    </Box>
  );

  // Mobile: temporary drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent drawer
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
      open
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
